// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { listInventory, updateInventoryItem, InventoryItemType, InventoryRow, createInventoryItem } from "@/integrations/supabase/inventory";
import { createProduct, updateProduct } from "@/integrations/supabase/products";
import { supabase } from "@/integrations/supabase/client";
import { useSync } from "@/contexts/SyncContext";
import { performanceMonitor } from "@/utils/performanceMonitor";
import { logger } from "@/utils/logger";
import { validateForm, validateMoney, validateName, validateQuantity } from "@/utils/validators";
import { formatCurrency } from "@/utils/formatCurrency";
import { criarMovimentacao } from "@/integrations/supabase/movimentacoes-estoque";
import {
  getInventoryAlertPreferences,
  listInventoryAlertLogs,
  triggerInventoryAlertsJob,
  upsertInventoryAlertPreferences,
  InventoryAlertPreferences,
  InventoryAlertLog,
} from "@/integrations/supabase/inventoryAlerts";
import { useAuth } from "@/components/AuthProvider";
import { getProductCategories, createProductCategory } from "@/integrations/supabase/taxonomies";
import { AddCategoryDialog } from "@/components/AddCategoryDialog";
import { SpecificationFields } from "@/components/SpecificationFields";
import {
  AlertTriangle,
  BellRing,
  Download,
  Filter,
  History,
  HelpCircle,
  PackageSearch,
  Package,
  Plus,
  TrendingDown,
  TrendingUp,
  Trash2,
  Edit,
  Info,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DialogDescription } from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ImportInventory } from "@/components/ImportInventory";
import { Checkbox } from "@/components/ui/checkbox";
import { useOnboardingHint } from "@/hooks/useOnboardingHint";

const ITEM_TYPE_OPTIONS: Array<{
  value: InventoryItemType;
  label: string;
  description: string;
  defaultUnit: string;
}> = [
  { value: "materia_prima", label: "Matéria-prima", description: "Linhas, botões, aviamentos, etc.", defaultUnit: "unidades" },
  { value: "tecido", label: "Tecido", description: "Rolôs, metros, retalhos", defaultUnit: "metros" },
  { value: "produto_acabado", label: "Produto acabado", description: "Peças prontas para venda", defaultUnit: "unidades" },
];

type NewItemState = {
  name: string;
  quantity: string;
  unit: string;
  minQuantity: string;
  itemType: InventoryItemType;
  category: string;
  supplier: string;
  costPerUnit: string;
  notes: string;
  lengthMeters: string;
  color: string;
  especificacoes?: Record<string, any>;
};

type EditItemState = NewItemState & { id?: string };

type AlertFormState = {
  email: string;
  whatsapp: string;
  send_email: boolean;
  send_whatsapp: boolean;
  notify_low: boolean;
  notify_critical: boolean;
  frequency: "daily" | "weekly";
};

const ALERT_DEFAULTS: AlertFormState = {
  email: "",
  whatsapp: "",
  send_email: true,
  send_whatsapp: false,
  notify_low: true,
  notify_critical: true,
  frequency: "daily",
};

function getItemTypeLabel(type: InventoryItemType) {
  return ITEM_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? "Item";
}

function computeStatusInfo(status: string) {
  switch (status) {
    case "critical":
      return {
        tone: "danger",
        badge: <Badge className="bg-destructive/15 text-destructive border border-destructive/30">Crítico</Badge>,
        icon: <AlertTriangle className="h-5 w-5 text-destructive" />,
        message: "Reposição urgente",
      };
    case "low":
      return {
        tone: "warning",
        badge: <Badge className="bg-amber-500/15 text-amber-600 border border-amber-500/40">Baixo</Badge>,
        icon: <TrendingDown className="h-5 w-5 text-amber-600" />,
        message: "Planeje reposição",
      };
    default:
      return {
        tone: "ok",
        badge: <Badge className="bg-emerald-500/15 text-emerald-600 border border-emerald-500/30">Ok</Badge>,
        icon: <TrendingUp className="h-5 w-5 text-emerald-600" />,
        message: "Estoque saudável",
      };
  }
}

function exportInventoryToCSV(items: InventoryRow[]) {
  const headers = [
    "Nome",
    "Tipo",
    "Quantidade",
    "Unidade",
    "Mínimo",
    "Custo Unitário",
    "Valor Total",
    "Fornecedor",
    "Categoria",
    "Status",
  ];

  const rows = items.map((item) => [
    item.name,
    getItemTypeLabel(item.item_type),
    String(item.quantity ?? 0),
    item.unit ?? "",
    String(item.min_quantity ?? 0),
    item.cost_per_unit ? Number(item.cost_per_unit).toFixed(2) : "",
    item.total_cost ? Number(item.total_cost).toFixed(2) : "",
    item.supplier ?? "",
    item.category ?? "",
    item.status ?? "",
  ]);

  const csv = [headers, ...rows].map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `estoque_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`);
  link.style.visibility = "hidden";
  link.style.position = "absolute";
  link.style.left = "-9999px";
  
  document.body.appendChild(link);
  link.click();
  
  // Remover o elemento de forma segura após um pequeno delay
  setTimeout(() => {
    try {
      if (link.parentNode === document.body) {
        document.body.removeChild(link);
      }
    } catch (e) {
      // Ignorar erro se o elemento já foi removido
      console.warn("Erro ao remover link de download:", e);
    }
    URL.revokeObjectURL(url);
  }, 100);
}

export default function Estoque() {
  const queryClient = useQueryClient();
  const { invalidateRelated } = useSync();
  const { empresa, user } = useAuth();
  const [newItemModalOpen, setNewItemModalOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryRow | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<InventoryItemType | "all">("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [isAlertsDialogOpen, setIsAlertsDialogOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [alertForm, setAlertForm] = useState<AlertFormState>(ALERT_DEFAULTS);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [createProductFromItem, setCreateProductFromItem] = useState(false);
  const { showHint: showOnboardingHint, hideHint: dismissOnboardingHint } = useOnboardingHint("estoque");

  const [newItemState, setNewItemState] = useState<NewItemState>({
    name: "",
    quantity: "0",
    unit: "unidades",
    minQuantity: "0",
    itemType: "materia_prima",
    category: "",
    supplier: "",
    costPerUnit: "",
    notes: "",
    lengthMeters: "",
    color: "",
    especificacoes: {},
  });

  const [editForm, setEditForm] = useState<EditItemState>({
    name: "",
    quantity: "",
    unit: "",
    minQuantity: "",
    itemType: "materia_prima",
    category: "",
    supplier: "",
    costPerUnit: "",
    notes: "",
    lengthMeters: "",
    color: "",
    especificacoes: {},
  });

  const {
    data: alertPreferencesData,
    isLoading: isLoadingAlertPreferences,
    isFetching: isFetchingAlertPreferences,
  } = useQuery({
    queryKey: ["inventoryAlertPreferences"],
    queryFn: getInventoryAlertPreferences,
    enabled: isAlertsDialogOpen,
  });

  const {
    data: categoriesData = [],
    isLoading: isCategoriesLoading,
  } = useQuery({
    queryKey: ["productCategories", empresa?.id],
    queryFn: () => empresa?.id ? getProductCategories(empresa.id) : Promise.resolve([]),
    enabled: !!empresa?.id,
  });

  const {
    data: alertLogs = [],
    isLoading: isLoadingAlertLogs,
    refetch: refetchAlertLogs,
  } = useQuery({
    queryKey: ["inventoryAlertLogs"],
    queryFn: () => listInventoryAlertLogs(40),
    enabled: isAlertsDialogOpen,
  });

  useEffect(() => {
    if (!isAlertsDialogOpen) {
      return;
    }
    if (alertPreferencesData) {
      setAlertForm({
        email: alertPreferencesData.email ?? "",
        whatsapp: alertPreferencesData.whatsapp ?? "",
        send_email: alertPreferencesData.send_email,
        send_whatsapp: alertPreferencesData.send_whatsapp,
        notify_low: alertPreferencesData.notify_low,
        notify_critical: alertPreferencesData.notify_critical,
        frequency: alertPreferencesData.frequency,
      });
    } else {
      setAlertForm(ALERT_DEFAULTS);
    }
  }, [alertPreferencesData, isAlertsDialogOpen]);

  const saveAlertsMutation = useMutation({
    mutationFn: upsertInventoryAlertPreferences,
    onSuccess: (result) => {
      if (result.ok) {
        toast.success("Preferências de alertas salvas!");
        queryClient.invalidateQueries({ queryKey: ["inventoryAlertPreferences"] });
      } else {
        toast.error(result.error || "Não foi possível salvar as preferências");
      }
    },
    onError: (error: unknown) => {
      toast.error((error as Error)?.message ?? "Erro ao salvar preferências");
    },
  });

  const triggerAlertsMutation = useMutation({
    mutationFn: triggerInventoryAlertsJob,
    onSuccess: (result) => {
      if (result.ok) {
        toast.success(result.message ?? "Alertas executados com sucesso");
        refetchAlertLogs();
      } else {
        toast.error(result.message ?? "Não foi possível executar os alertas");
      }
    },
    onError: (error: unknown) => {
      toast.error((error as Error)?.message ?? "Erro ao executar alertas");
    },
  });

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["inventory"],
    queryFn: listInventory,
  });

  // Normalizar texto para busca (remove acentos, espaços extras, caixa)
  const normalizeSearch = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/\s+/g, " ")
      .trim();

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim() && categoryFilter === "all") {
      // Se não há termo de busca nem filtro de categoria, apenas filtrar por tipo
      return items.filter((item) => typeFilter === "all" || item.item_type === typeFilter);
    }

    const search = normalizeSearch(searchTerm);
    
    return items.filter((item) => {
      const matchesType = typeFilter === "all" || item.item_type === typeFilter;
      const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
      
      if (!matchesType || !matchesCategory) return false;

      if (!search) return true;

      const searchWords = search.split(" ");

      // Buscar em múltiplos campos: nome, fornecedor, categoria
      const searchableText = [
        item.name || "",
        item.supplier || "",
        item.category || "",
      ]
        .join(" ")
        .toString();

      const normalizedText = normalizeSearch(searchableText);

      // Cada palavra digitada deve existir em alguma parte do texto
      const matchesSearch = searchWords.every((word) => normalizedText.includes(word));

      return matchesSearch;
    });
  }, [items, typeFilter, categoryFilter, searchTerm]);

  const summary = useMemo(() => {
    const totalItems = filteredItems.length;
    const totalQuantity = filteredItems.reduce((acc, item) => acc + Number(item.quantity ?? 0), 0);
    const totalValue = filteredItems.reduce((acc, item) => acc + Number(item.total_cost ?? (item.cost_per_unit ?? 0) * (item.quantity ?? 0)), 0);
    const criticalCount = filteredItems.filter((item) => item.status === "critical").length;
    const lowCount = filteredItems.filter((item) => item.status === "low").length;

    const byType = filteredItems.reduce<Record<InventoryItemType, { count: number; value: number }>>(
      (acc, item) => {
        const current = acc[item.item_type] ?? { count: 0, value: 0 };
        current.count += 1;
        current.value += Number(item.total_cost ?? (item.cost_per_unit ?? 0) * (item.quantity ?? 0));
        acc[item.item_type] = current;
        return acc;
      },
      {
        materia_prima: { count: 0, value: 0 },
        tecido: { count: 0, value: 0 },
        produto_acabado: { count: 0, value: 0 },
      }
    );

    return { totalItems, totalQuantity, totalValue, criticalCount, lowCount, byType };
  }, [filteredItems]);

  const resetNewItemState = () =>
    setNewItemState({
      name: "",
      quantity: "0",
      unit: "unidades",
      minQuantity: "0",
      itemType: "materia_prima",
      category: "",
      supplier: "",
      costPerUnit: "",
      notes: "",
      lengthMeters: "",
      color: "",
    });

  const handleAddCategory = async (categoryName: string) => {
    if (!empresa?.id || !user?.id) return;
    
    try {
      logger.info("📦 Criando categoria de produto", { categoryName });
      await createProductCategory(empresa.id, categoryName, user.id);
      toast.success(`Categoria "${categoryName}" adicionada!`);
      queryClient.invalidateQueries({ queryKey: ["productCategories", empresa.id] });
    } catch (error) {
      logger.error("❌ Erro ao criar categoria", { error, categoryName });
      toast.error("Erro ao adicionar categoria");
    }
  };

  const handleCreateItem = async () => {
    const numericQuantity = parseFloat(newItemState.quantity.replace(",", ".")) || 0;
    const numericMin = parseFloat(newItemState.minQuantity.replace(",", ".")) || 0;
    const numericCost = newItemState.costPerUnit ? parseFloat(newItemState.costPerUnit.replace(",", ".")) : null;
    const numericLength = newItemState.lengthMeters ? parseFloat(newItemState.lengthMeters.replace(",", ".")) : null;

    const validation = validateForm(
      {
        name: newItemState.name,
        quantity: numericQuantity,
        min: numericMin,
        cost: numericCost ?? 0,
      },
      {
        name: validateName,
        quantity: validateQuantity,
        min: (value) =>
          value >= 0 ? { isValid: true, errors: [] } : { isValid: false, errors: ["Quantidade mínima não pode ser negativa"] },
        cost: validateMoney,
      }
    );

    if (!validation.isValid) {
      validation.errors.forEach((error) => toast.error(error));
      return;
    }

    const metadata: Record<string, unknown> = {};
    if (newItemState.itemType === "tecido") {
      if (numericLength && numericLength > 0) metadata.length_meters = numericLength;
      if (newItemState.color) metadata.color = newItemState.color;
    }
    if (newItemState.notes) metadata.notes = newItemState.notes;

    const { data: userEmpresa, error: empresaError } = await supabase
      .from("user_empresas")
      .select("empresa_id")
      .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
      .single();

    if (empresaError || !userEmpresa?.empresa_id) {
      toast.error("Não foi possível identificar a empresa do usuário");
      return;
    }
    
    try {
      // Criar item de estoque
      const inventoryResult = await createInventoryItem({
        name: newItemState.name.trim(),
        unit: newItemState.unit.trim() ||
          ITEM_TYPE_OPTIONS.find((i) => i.value === newItemState.itemType)?.defaultUnit ||
          "unidades",
        quantity: numericQuantity,
        min_quantity: numericMin,
        item_type: newItemState.itemType,
        category: newItemState.category?.trim() || null,
        supplier: newItemState.supplier?.trim() || null,
        cost_per_unit: numericCost,
        metadata,
      });

      if (!inventoryResult.ok || !inventoryResult.id) {
        toast.error(inventoryResult.error || "Não foi possível salvar o item. Tente novamente.");
        return;
      }

      const inventoryItemId = inventoryResult.id;

      // Se checkbox marcado, criar produto no catálogo e vincular
      if (createProductFromItem) {
        try {
          // Determinar tipo de produto baseado no tipo de item
          let productType = "Personalizado";
          if (newItemState.itemType === "produto_acabado") {
            productType = "Personalizado";
          } else if (newItemState.itemType === "tecido") {
            productType = "Estampado";
          }

          // Criar produto
          const productResult = await createProduct({
            name: newItemState.name.trim(),
            type: productType,
            materials: newItemState.category ? [newItemState.category] : [],
            work_hours: 0,
            unit_price: numericCost ? numericCost * 2 : 0, // Preço padrão: 2x o custo
            profit_margin: 0,
          });

          if (productResult.ok && productResult.id) {
            // Vincular item de estoque ao produto
            await updateProduct(productResult.id, {
              inventory_items: [inventoryItemId],
              inventory_quantities: [1], // 1 unidade do item por unidade do produto
            });
            toast.success("Item de estoque e produto criados e vinculados com sucesso!");
          } else {
            toast.success("Item de estoque criado, mas houve erro ao criar produto no catálogo.");
          }
        } catch (productError) {
          console.error("Erro ao criar produto:", productError);
          toast.success("Item de estoque criado, mas houve erro ao criar produto no catálogo.");
        }
      } else {
        toast.success("Item adicionado com sucesso!");
      }

      logger.userAction("inventory_item_created", "ESTOQUE", {
        name: newItemState.name,
        type: newItemState.itemType,
        quantity: numericQuantity,
        createdProduct: createProductFromItem,
      });
      
      resetNewItemState();
      setCreateProductFromItem(false);
      setNewItemModalOpen(false);
      invalidateRelated("inventory_items");
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    } catch (error: unknown) {
      console.error("Erro ao criar item de estoque:", error);
      const message = error instanceof Error ? error.message : "Erro inesperado ao salvar item";
      toast.error(message);
    }
  };

  const handleEditItem = (item: InventoryRow) => {
    setEditingItem(item);
    const metadata = (item.metadata ?? {}) as Record<string, unknown>;
    setEditForm({
      id: item.id,
      name: item.name,
      quantity: String(item.quantity ?? 0),
      unit: item.unit ?? "",
      minQuantity: String(item.min_quantity ?? 0),
      itemType: item.item_type,
      category: item.category ?? "",
      supplier: item.supplier ?? "",
      costPerUnit: item.cost_per_unit ? String(item.cost_per_unit) : "",
      notes: typeof metadata?.notes === "string" ? metadata.notes : "",
      lengthMeters: typeof metadata?.length_meters === "number" ? String(metadata.length_meters) : "",
      color: typeof metadata?.color === "string" ? metadata.color : "",
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingItem || !editForm.id) {
      toast.error("Item inválido para edição");
      return;
    }
    
    const numericQuantity = parseFloat(editForm.quantity.replace(",", ".")) || 0;
    const numericMin = parseFloat(editForm.minQuantity.replace(",", ".")) || 0;
    const numericCost = editForm.costPerUnit ? parseFloat(editForm.costPerUnit.replace(",", ".")) : null;
    const numericLength = editForm.lengthMeters ? parseFloat(editForm.lengthMeters.replace(",", ".")) : null;

    const metadata: Record<string, unknown> = {};
    if (editForm.itemType === "tecido") {
      if (numericLength && numericLength > 0) metadata.length_meters = numericLength;
      if (editForm.color) metadata.color = editForm.color;
    }
    if (editForm.notes) metadata.notes = editForm.notes;

    // 1) Atualizar metadados/campos (sem mexer direto no saldo)
    const result = await updateInventoryItem(editForm.id, {
      name: editForm.name,
      unit: editForm.unit,
      min_quantity: numericMin,
      item_type: editForm.itemType,
      category: editForm.category || null,
      supplier: editForm.supplier || null,
      cost_per_unit: numericCost,
      metadata,
    });
      
    if (!result.ok) {
      toast.error(result.error || "Erro ao atualizar item");
      return;
    }

    // 2) Ajustar saldo via movimentação (auditoria) se necessário
    const previousQuantity = Number(editingItem.quantity ?? 0);
    const delta = numericQuantity - previousQuantity;
    if (delta !== 0) {
      const ajusteSign = delta >= 0 ? "incremento" : "decremento";
      const ajusteQuantidade = Math.abs(delta);

      const mov = await criarMovimentacao({
        inventory_item_id: editForm.id,
        tipo_movimentacao: "ajuste",
        ajuste_sign: ajusteSign,
        quantidade: ajusteQuantidade,
        motivo: `Ajuste manual via edição do item: ${editForm.name}`,
        origem: "ajuste_manual",
        origem_id: null,
      } as any);

      if (!mov.ok) {
        toast.warning("Item atualizado, mas não foi possível registrar a movimentação de ajuste");
      }
    }

    toast.success("Item atualizado com sucesso");
    setIsEditDialogOpen(false);
    setEditingItem(null);
    invalidateRelated("inventory_items");
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
  };

  const handleDeleteItem = async (item: InventoryRow) => {
    if (!item.id) {
      toast.error("Item inválido");
          return;
        }
    if (!confirm(`Tem certeza que deseja excluir "${item.name}"?`)) return;

    const { error } = await supabase.from("inventory_items").delete().eq("id", item.id);
    if (error) {
      toast.error("Erro ao excluir item");
          return;
        }

    toast.success("Item excluído com sucesso");
    invalidateRelated("inventory_items");
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
  };

  // Funções para seleção múltipla
  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => 
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const selectAll = () => {
    setSelectedItems(filteredItems.map(item => item.id));
  };

  const deselectAll = () => {
    setSelectedItems([]);
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      toast.error("Selecione pelo menos um item para excluir");
      return;
    }
    
    const confirmMessage = `Tem certeza que deseja excluir ${selectedItems.length} item(ns) de estoque?\n\nEsta ação não pode ser desfeita!`;
    if (!confirm(confirmMessage)) {
      return;
    }
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const itemId of selectedItems) {
      try {
        const { error } = await supabase.from("inventory_items").delete().eq("id", itemId);
        if (error) {
          errorCount++;
          logger.error(`Erro ao excluir item ${itemId}:`, error);
        } else {
          successCount++;
        }
      } catch (error) {
        errorCount++;
        logger.error(`Erro ao excluir item ${itemId}:`, error);
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} item(ns) excluído(s) com sucesso!`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} item(ns) não puderam ser excluídos`);
    }
    
    setSelectedItems([]);
    setIsSelecting(false);
    invalidateRelated("inventory_items");
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
  };

  const handleCreateProductsFromSelectedItems = async () => {
    console.error("🚀 ===== INICIANDO CRIAÇÃO DE PRODUTOS =====");
    console.error("📋 Itens selecionados:", selectedItems);
    console.error("📋 Total de itens selecionados:", selectedItems.length);
    
    if (selectedItems.length === 0) {
      console.error("⚠️ Nenhum item selecionado");
      toast.error("Selecione pelo menos um item para criar produto");
      return;
    }

    console.error("✅ Validação passou, buscando itens do banco...");
    toast.info(`Buscando ${selectedItems.length} item(ns) do banco de dados...`, { duration: 2000 });

    // Buscar itens atualizados do banco para garantir que temos os dados mais recentes
    console.error("🔍 Fazendo query no Supabase...");
    const { data: currentItems, error: fetchError } = await supabase
      .from("inventory_items")
      .select("*")
      .in("id", selectedItems);

    console.error("🔍 Resultado da busca:", { 
      temItens: !!currentItems, 
      quantidade: currentItems?.length, 
      temErro: !!fetchError,
      erro: fetchError 
    });

    if (fetchError) {
      console.error("❌ Erro ao buscar itens:", fetchError);
      toast.error(`Erro ao buscar itens: ${fetchError.message}`);
      return;
    }

    if (!currentItems || currentItems.length === 0) {
      console.error("⚠️ Nenhum item encontrado no banco");
      toast.error("Nenhum item válido encontrado");
      return;
    }

    console.error(`✅ ${currentItems.length} item(ns) encontrado(s) no banco`);

    console.error("🔄 Processando itens...");
    const selectedItemsData = currentItems.map(item => ({
      id: item.id,
      name: item.name || "",
      item_type: item.item_type || "materia_prima",
      category: item.category || null,
      supplier: item.supplier || null,
      cost_per_unit: item.cost_per_unit || null,
    })).filter(item => item.name.trim() !== "");
    
    console.error("📦 Itens processados:", selectedItemsData);
    
    if (selectedItemsData.length === 0) {
      console.error("❌ Nenhum item válido após processamento");
      toast.error("Nenhum item válido encontrado (itens sem nome)");
      return;
    }

    console.error(`✅ ${selectedItemsData.length} item(ns) válido(s) para processar`);

    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    console.error(`🔄 Iniciando processamento de ${selectedItemsData.length} item(ns)...`);
    toast.loading(`Processando ${selectedItemsData.length} item(ns)...`, { id: "creating-products" });

    for (const item of selectedItemsData) {
      try {
        console.error(`🔄 Processando item: ${item.name} (${item.id})`, item);
        
        // Validar dados do item
        if (!item.name || item.name.trim() === "") {
          console.error(`⚠️ Item sem nome, pulando: ${item.id}`);
          errorCount++;
          errors.push(`Item sem nome (ID: ${item.id})`);
          continue;
        }

        if (!item.id) {
          console.error(`⚠️ Item sem ID, pulando: ${item.name}`);
          errorCount++;
          errors.push(`${item.name}: Item sem ID`);
          continue;
        }
        
        console.error(`🔍 Verificando se produto "${item.name}" já existe...`);
        
        // Obter empresa_id do usuário para buscar apenas produtos da mesma empresa
        const { data: userEmpresa } = await supabase
          .from("user_empresas")
          .select("empresa_id")
          .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
          .single();
        
        const userEmpresaId = userEmpresa?.empresa_id;
        console.error(`🏢 Buscando produtos da empresa: ${userEmpresaId}`);
        
        // Verificar se já existe produto com esse nome E mesma empresa
        const { data: existingProducts, error: searchError } = await supabase
          .from("atelie_products")
          .select("id, empresa_id, name")
          .eq("name", item.name.trim())
          .eq("empresa_id", userEmpresaId || "")
          .limit(1);

        if (searchError) {
          console.error(`❌ Erro ao buscar produto existente:`, searchError);
          errorCount++;
          errors.push(`${item.name}: Erro ao buscar produto existente`);
          continue;
        }

        console.error(`📊 Resultado da busca:`, { 
          existe: existingProducts && existingProducts.length > 0,
          quantidade: existingProducts?.length 
        });

        if (existingProducts && existingProducts.length > 0) {
          // Se já existe, apenas vincular
          const productId = existingProducts[0].id;
          console.error(`✅ Produto já existe: ${item.name} (${productId}), vinculando item...`);
          
          // Verificar empresa_id do produto existente
          const { data: productFullData } = await supabase
            .from("atelie_products")
            .select("id, empresa_id, name")
            .eq("id", productId)
            .single();
          
          console.error(`🏢 Produto existente - empresa_id:`, productFullData?.empresa_id);
          
          // Verificar empresa_id atual do usuário
          const { data: userEmpresa } = await supabase
            .from("user_empresas")
            .select("empresa_id")
            .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
            .single();
          
          console.error(`🏢 Empresa do usuário:`, userEmpresa?.empresa_id);
          
          if (productFullData?.empresa_id !== userEmpresa?.empresa_id) {
            console.error(`⚠️ ATENÇÃO: Produto existe mas empresa_id diferente! Produto: ${productFullData?.empresa_id}, Usuário: ${userEmpresa?.empresa_id}`);
            console.error(`⚠️ Por isso o produto não aparece no catálogo!`);
            errorCount++;
            errors.push(`${item.name}: Produto existe mas pertence a outra empresa`);
            continue;
          }
          
          const { data: productData, error: fetchError } = await supabase
            .from("atelie_products")
            .select("inventory_items, inventory_quantities")
            .eq("id", productId)
            .single();

          if (fetchError) {
            console.error(`❌ Erro ao buscar dados do produto:`, fetchError);
            errorCount++;
            errors.push(`${item.name}: Erro ao buscar dados do produto`);
            continue;
          }

          let inventoryItems: string[] = [];
          let inventoryQuantities: number[] = [];

          if (productData) {
            if (typeof productData.inventory_items === 'string') {
              try {
                inventoryItems = JSON.parse(productData.inventory_items);
              } catch {
                inventoryItems = [];
              }
            } else if (Array.isArray(productData.inventory_items)) {
              inventoryItems = productData.inventory_items;
            }

            if (typeof productData.inventory_quantities === 'string') {
              try {
                inventoryQuantities = JSON.parse(productData.inventory_quantities);
              } catch {
                inventoryQuantities = [];
              }
            } else if (Array.isArray(productData.inventory_quantities)) {
              inventoryQuantities = productData.inventory_quantities;
            }
          }

          // Adicionar item se não estiver já vinculado
          const jaEstavaVinculado = inventoryItems.includes(item.id);
          if (!jaEstavaVinculado) {
            inventoryItems.push(item.id);
            inventoryQuantities.push(1);
            console.error(`➕ Adicionando item ${item.id} aos vínculos`);
            
            console.error(`💾 Salvando vínculos no produto ${productId}...`);
            const updateResult = await updateProduct(productId, {
              inventory_items: inventoryItems,
              inventory_quantities: inventoryQuantities,
            });

            if (updateResult.ok) {
              successCount++;
              console.error(`✅ Item ${item.name} vinculado com sucesso`);
            } else {
              errorCount++;
              errors.push(`${item.name}: ${updateResult.error || "Erro ao vincular"}`);
              console.error(`❌ Erro ao vincular item:`, updateResult.error);
            }
          } else {
            console.error(`ℹ️ Item ${item.name} já estava vinculado ao produto - pulando`);
            successCount++; // Conta como sucesso pois já está correto
          }
        } else {
          // Criar novo produto
          console.error(`🆕 Criando novo produto: ${item.name}`);
          
          let productType = "Personalizado";
          if (item.item_type === "produto_acabado") {
            productType = "Personalizado";
          } else if (item.item_type === "tecido") {
            productType = "Estampado";
          }

          console.error(`📝 Chamando createProduct com:`, {
            name: item.name,
            type: productType,
            materials: item.category ? [item.category] : [],
          });
          
          const productResult = await createProduct({
            name: item.name,
            type: productType,
            materials: item.category ? [item.category] : [],
            work_hours: 0,
            unit_price: item.cost_per_unit ? item.cost_per_unit * 2 : 0,
            profit_margin: 0,
          });

          console.error(`📊 Resultado do createProduct:`, {
            ok: productResult.ok,
            id: productResult.id,
            error: productResult.error,
            data: productResult.data
          });

          // Verificar empresa_id do produto criado
          if (productResult.ok && productResult.id && productResult.data) {
            console.error(`🏢 Produto criado com empresa_id:`, productResult.data.empresa_id);
            
            // Verificar empresa_id atual do usuário
            const { data: userEmpresa } = await supabase
              .from("user_empresas")
              .select("empresa_id")
              .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
              .single();
            
            console.error(`🏢 Empresa do usuário:`, userEmpresa?.empresa_id);
            
            if (productResult.data.empresa_id !== userEmpresa?.empresa_id) {
              console.error(`⚠️ ATENÇÃO: empresa_id do produto (${productResult.data.empresa_id}) diferente da empresa do usuário (${userEmpresa?.empresa_id})`);
            }

            // Verificar se o produto pode ser encontrado imediatamente após criar
            console.error(`🔍 Verificando se produto pode ser encontrado após criação...`);
            const { data: produtoVerificado, error: erroVerificacao } = await supabase
              .from("atelie_products")
              .select("id, name, empresa_id")
              .eq("id", productResult.id)
              .eq("empresa_id", userEmpresa?.empresa_id || "")
              .single();
            
            if (erroVerificacao) {
              console.error(`❌ ERRO: Produto criado mas não pode ser encontrado!`, erroVerificacao);
            } else {
              console.error(`✅ Produto pode ser encontrado após criação:`, produtoVerificado);
            }
          }

          if (productResult.ok && productResult.id) {
            console.error(`✅ Produto criado: ${item.name} (${productResult.id})`);
            
            // Vincular item de estoque ao produto
            console.error(`🔗 Vinculando item ${item.id} ao produto ${productResult.id}...`);
            const linkResult = await updateProduct(productResult.id, {
              inventory_items: [item.id],
              inventory_quantities: [1],
            });

            console.error(`📊 Resultado do updateProduct (vínculo):`, {
              ok: linkResult.ok,
              error: linkResult.error
            });

            if (linkResult.ok) {
              successCount++;
              console.error(`✅ Item vinculado ao produto criado`);
            } else {
              errorCount++;
              errors.push(`${item.name}: Produto criado mas erro ao vincular - ${linkResult.error}`);
              console.error(`❌ Erro ao vincular item ao produto criado:`, linkResult.error);
            }
          } else {
            errorCount++;
            errors.push(`${item.name}: ${productResult.error || "Erro ao criar produto"}`);
            console.error(`❌ Erro ao criar produto:`, productResult.error);
          }
        }
      } catch (error) {
        errorCount++;
        const errorMessage = error instanceof Error ? error.message : "Erro desconhecido";
        errors.push(`${item.name}: ${errorMessage}`);
        logger.error(`Erro ao criar produto para item ${item.id}:`, error);
        console.error(`❌ Erro inesperado ao processar item ${item.name}:`, error);
        console.error(`❌ Stack trace:`, error instanceof Error ? error.stack : "N/A");
      }
    }

    toast.dismiss("creating-products");

    console.error("📊 ===== RESULTADO FINAL =====");
    console.error(`✅ Sucessos: ${successCount}`);
    console.error(`❌ Erros: ${errorCount}`);
    console.error("📝 Lista de erros:", errors);

    if (successCount > 0) {
      const message = errorCount === 0 
        ? `${successCount} item(ns) processado(s) com sucesso!`
        : `${successCount} item(ns) processado(s), ${errorCount} com erro`;
      toast.success(message);
      
      // Invalidar cache e forçar refetch
      console.error("🔄 Invalidando cache de produtos...");
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.refetchQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      
      // Verificar se os produtos criados podem ser encontrados
      console.error("🔍 Verificando se produtos criados podem ser encontrados...");
      const { data: userEmpresa } = await supabase
        .from("user_empresas")
        .select("empresa_id")
        .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
        .single();
      
      if (userEmpresa?.empresa_id) {
        const produtosCriados = selectedItemsData.filter(item => {
          // Verificar se produto foi criado (não apenas vinculado)
          return true; // Por enquanto verificar todos
        });
        
        for (const item of produtosCriados.slice(0, 3)) { // Verificar apenas os 3 primeiros
          const { data: produtoEncontrado } = await supabase
            .from("atelie_products")
            .select("id, name, empresa_id")
            .eq("name", item.name)
            .eq("empresa_id", userEmpresa.empresa_id)
            .limit(1);
          
          console.error(`🔍 Produto "${item.name}":`, {
            encontrado: !!produtoEncontrado && produtoEncontrado.length > 0,
            id: produtoEncontrado?.[0]?.id,
            empresa_id: produtoEncontrado?.[0]?.empresa_id
          });
        }
      }
      
      setSelectedItems([]);
      setIsSelecting(false);
    }
    
    if (errorCount > 0) {
      const errorDetails = errors.slice(0, 5).join("; ");
      const moreErrors = errors.length > 5 ? ` e mais ${errors.length - 5} erro(s)` : "";
      toast.error(`${errorCount} produto(s) não puderam ser criados: ${errorDetails}${moreErrors}`, {
        duration: 10000,
      });
      console.error("❌ Erros detalhados:", errors);
    }

    if (successCount === 0 && errorCount === 0) {
      console.error("⚠️ Nenhum item foi processado");
      toast.warning("Nenhum item foi processado");
    }

    console.error("🏁 ===== FIM DA CRIAÇÃO DE PRODUTOS =====");
  };

  return (
    <div className="min-h-screen bg-muted/20">
      <PageHeader
        title="Estoque"
        description="Controle centralizado de matéria-prima, tecidos e produtos"
        icon={<Package className="w-6 h-6 text-purple-600" />}
        statusIndicatorsAbove
        leftActions={
          <>
            <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2">
                  <HelpCircle className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="hidden sm:inline ml-1">Ajuda</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Ajuda rápida — Estoque</DialogTitle>
                  <DialogDescription>
                    O Estoque é onde você controla matérias-primas, tecidos e produtos acabados. Depois, no Catálogo, você vincula os produtos aos itens do estoque para medir consumo.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 text-sm">
                  <div className="space-y-2">
                    <p className="font-semibold">1) Cadastre seus itens principais</p>
                    <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                      <li>Crie itens como: “Tecido Algodão”, “Linha preta”, “Zíper 20cm”, “Botão 18mm”.</li>
                      <li>Escolha bem a <strong>unidade</strong> (metros, unidades, kg). Isso melhora a leitura e evita erro no consumo.</li>
                      <li>Defina <strong>mínimo</strong> para o sistema te avisar antes de faltar.</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <p className="font-semibold">2) Importar estoque (se você já tem planilha)</p>
                    <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                      <li>Use o botão de importação aqui no Estoque para subir seus itens em lote.</li>
                      <li>Depois, no Catálogo, você pode importar produtos já com “Item Estoque” e “Qtd/Un” para vincular automaticamente.</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <p className="font-semibold">3) Alertas automáticos</p>
                    <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                      <li>Ative “Alertas” para receber por e-mail quando um item ficar baixo/crítico.</li>
                      <li>Se estiver testando, use “Executar agora”.</li>
                    </ul>
                  </div>

                  <div className="rounded-lg border bg-muted/10 p-4 text-muted-foreground">
                    <p className="font-semibold text-foreground mb-1">Dica prática</p>
                    <p>
                      Padronize nomes e unidades. Ex: sempre “metros” para tecido e “unidades” para aviamentos. Isso deixa a importação e a vinculação muito mais “perfeitas”.
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {isSelecting && selectedItems.length > 0 && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.error("🔘 BOTÃO CLICADO! Itens selecionados:", selectedItems);
                    handleCreateProductsFromSelectedItems();
                  }}
                >
                  <Package className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="hidden sm:inline ml-1">Criar produto no Catálogo ({selectedItems.length})</span>
                  <span className="sm:hidden ml-1">Produto ({selectedItems.length})</span>
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm"
                  className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="hidden sm:inline ml-1">Excluir {selectedItems.length}</span>
                  <span className="sm:hidden ml-1">Exc.</span>
                </Button>
              </>
            )}
            {isSelecting && (
              <Button 
                variant="outline" 
                size="sm"
                className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                onClick={() => {
                  setIsSelecting(false);
                  setSelectedItems([]);
                }}
              >
                Cancelar
              </Button>
            )}
            {!isSelecting && (
              <Button 
                variant="outline" 
                size="sm"
                className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                onClick={() => setIsSelecting(true)}
              >
                Selecionar
              </Button>
            )}
            <Dialog
              open={isAlertsDialogOpen}
              onOpenChange={(open) => {
                setIsAlertsDialogOpen(open);
                if (!open) {
                  setAlertForm(ALERT_DEFAULTS);
                }
              }}
            >
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2">
                  <BellRing className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="hidden sm:inline ml-1">Alertas</span>
              </Button>
            </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
                <div className="flex min-h-full flex-col">
                  <DialogHeader className="sticky top-0 z-10 bg-card px-6 pt-6 pb-4">
                    <DialogTitle>Alertas automáticos de estoque</DialogTitle>
                    <p className="text-sm text-muted-foreground">
                      Receba por e-mail os itens que atingirem níveis baixo ou crítico.
                    </p>
              </DialogHeader>
                  <div className="flex-1 px-6">
                    <div className="space-y-6 pb-6">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-3 rounded-lg border border-border/60 bg-muted/10 p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="font-semibold text-foreground">Enviar por e-mail</p>
                              <p className="text-sm text-muted-foreground">
                                Um resumo com o estoque baixo ou crítico será enviado automaticamente.
                              </p>
                </div>
                            <Switch
                              checked={alertForm.send_email}
                              onCheckedChange={(checked) => setAlertForm((prev) => ({ ...prev, send_email: checked }))}
                              disabled={isLoadingAlertPreferences || saveAlertsMutation.isPending}
                            />
                </div>
                          <div className="space-y-2">
                            <Label htmlFor="alert-email">E-mail principal</Label>
                            <Input
                              id="alert-email"
                              type="email"
                              placeholder="contato@empresa.com.br"
                              value={alertForm.email}
                              onChange={(event) => setAlertForm((prev) => ({ ...prev, email: event.target.value }))}
                              disabled={!alertForm.send_email}
                            />
                            <p className="text-xs text-muted-foreground">
                              Você receberá um único resumo com todos os itens em atenção.
                            </p>
                            {alertForm.send_email && alertForm.email && (
                              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                                <strong>ℹ️ Importante:</strong> Os alertas são executados automaticamente uma vez por dia. 
                                Você também pode clicar em "Executar agora" abaixo para testar imediatamente.
                              </div>
                            )}
                </div>
                </div>
                        <div className="space-y-3 rounded-lg border border-border/60 bg-muted/10 p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="font-semibold text-foreground">WhatsApp (em breve)</p>
                              <p className="text-sm text-muted-foreground">Alertas instantâneos pelo WhatsApp chegam na próxima versão.</p>
              </div>
                            <Switch checked={false} disabled />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="alert-whatsapp">Número com DDD</Label>
                            <Input
                              id="alert-whatsapp"
                              placeholder="(11) 99999-9999"
                              value={alertForm.whatsapp}
                              onChange={(event) => setAlertForm((prev) => ({ ...prev, whatsapp: event.target.value }))}
                              disabled
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4 rounded-lg border border-border/60 bg-muted/10 p-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="font-semibold text-foreground">Alertar estoque baixo</p>
                              <p className="text-sm text-muted-foreground">
                                Envia quando a quantidade ficar abaixo do mínimo que você definiu.
                              </p>
                            </div>
                            <Switch
                              checked={alertForm.notify_low}
                              onCheckedChange={(checked) => setAlertForm((prev) => ({ ...prev, notify_low: checked }))}
                            />
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="font-semibold text-foreground">Alertar estoque crítico</p>
                              <p className="text-sm text-muted-foreground">
                                Envia quando o item zerar ou ficar negativo.
                              </p>
                            </div>
                            <Switch
                              checked={alertForm.notify_critical}
                              onCheckedChange={(checked) =>
                                setAlertForm((prev) => ({ ...prev, notify_critical: checked }))
                              }
                            />
                          </div>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Frequência</Label>
                            <Select
                              value={alertForm.frequency}
                              onValueChange={(value: "daily" | "weekly") =>
                                setAlertForm((prev) => ({ ...prev, frequency: value }))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione uma opção" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="daily">Diariamente às 08h</SelectItem>
                                <SelectItem value="weekly">Semanalmente (segunda-feira)</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                              O horário considera o fuso de Brasília. Ajustes finos estarão disponíveis em breve.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 pb-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                              Eventos recentes
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Últimos alertas disparados automaticamente.
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => refetchAlertLogs()}
                            disabled={isLoadingAlertLogs}
                          >
                            Atualizar
                          </Button>
                        </div>
                        {isLoadingAlertLogs ? (
                          <div className="rounded-md border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                            Carregando eventos...
                          </div>
                        ) : alertLogs.length === 0 ? (
                          <div className="rounded-md border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                            Nenhum alerta enviado até agora.
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {alertLogs.map((log) => {
                              const payload = (log.payload ?? {}) as Record<string, unknown>;
                              const quantity = payload.quantity ?? payload.quantidade ?? "?";
                              const minQuantity = payload.min_quantity ?? payload.min ?? "?";
                              return (
                                <div key={log.id} className="rounded-lg border border-border/60 bg-background p-3">
                                  <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                      <Badge
                                        className={
                                          log.status === "critical"
                                            ? "bg-destructive/15 text-destructive border border-destructive/30"
                                            : "bg-amber-500/15 text-amber-600 border border-amber-500/30"
                                        }
                                      >
                                        {log.status === "critical" ? "Estoque crítico" : "Estoque baixo"}
                                      </Badge>
                                      <span className="text-xs text-muted-foreground">
                                        {new Date(log.sent_at).toLocaleString("pt-BR")}
                                      </span>
                                    </div>
                                  </div>
                                  <p className="mt-2 text-sm font-semibold text-foreground">
                                    {(payload.name as string) ?? "Item sem nome"}
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    Saldo atual: {quantity} • Mínimo: {minQuantity}
                                  </p>
                                  {payload.supplier && (
                                    <p className="text-xs text-muted-foreground/80">
                                      Fornecedor: {payload.supplier as string}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <DialogFooter className="sticky bottom-0 z-10 border-t border-border/70 bg-card px-6 py-4">
                    <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <Button
                        variant="ghost"
                        type="button"
                        onClick={() => {
                          setIsAlertsDialogOpen(false);
                          setAlertForm(ALERT_DEFAULTS);
                        }}
                        className="w-full sm:w-auto"
                      >
                        Fechar
                      </Button>
                      <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={async () => {
                            await triggerAlertsMutation.mutateAsync();
                          }}
                          disabled={
                            triggerAlertsMutation.isPending ||
                            isLoadingAlertPreferences ||
                            isFetchingAlertPreferences
                          }
                          className="w-full sm:w-auto"
                        >
                          {triggerAlertsMutation.isPending ? (
                            "Executando..."
                          ) : (
                            <>
                              <History className="mr-2 h-4 w-4" />
                              Executar agora
                            </>
                          )}
                        </Button>
                        <Button
                          type="button"
                          onClick={async () => {
                            const payload: InventoryAlertPreferences = {
                              ...(alertPreferencesData ?? {}),
                              ...alertForm,
                            };
                            await saveAlertsMutation.mutateAsync(payload);
                          }}
                          disabled={
                            saveAlertsMutation.isPending ||
                            triggerAlertsMutation.isPending ||
                            isLoadingAlertPreferences ||
                            isFetchingAlertPreferences ||
                            (!alertForm.notify_low && !alertForm.notify_critical)
                          }
                          className="w-full sm:w-auto"
                        >
                          {saveAlertsMutation.isPending ? "Salvando..." : "Salvar alertas"}
                </Button>
                      </div>
                    </div>
              </DialogFooter>
                </div>
            </DialogContent>
          </Dialog>
            <ImportInventory onImportComplete={() => {
              queryClient.invalidateQueries({ queryKey: ["inventory"] });
            }} />
            <Button variant="outline" size="sm" className="text-xs sm:text-sm" onClick={() => exportInventoryToCSV(filteredItems)}>
              <Download className="w-4 h-4 mr-1 md:mr-2 flex-shrink-0" />
              <span className="hidden md:inline">Exportar CSV</span>
              <span className="md:hidden">CSV</span>
            </Button>
          </>
        }
        rightActions={
          <Dialog open={newItemModalOpen} onOpenChange={(open) => (open ? setNewItemModalOpen(true) : setNewItemModalOpen(false))}>
            <DialogTrigger asChild>
              <Button size="sm" className="text-xs md:text-sm bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 hover:scale-105 transition-all duration-200 text-white font-medium shadow-md">
                <Plus className="w-4 h-4 mr-1 md:mr-2 flex-shrink-0" />
                <span className="hidden md:inline">Novo item</span>
                <span className="md:hidden">Novo</span>
              </Button>
            </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader className="sticky top-0 z-10 bg-card pb-4">
                  <DialogTitle>Novo item de estoque</DialogTitle>
                </DialogHeader>
                <div className="grid gap-6 py-2">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Nome *</Label>
                      <Input
                        value={newItemState.name}
                        onChange={(event) => setNewItemState((prev) => ({ ...prev, name: event.target.value }))}
                        placeholder="Ex: Tecido algodão azul"
                      />
        </div>
                    <div className="space-y-2">
                      <Label>Categoria</Label>
                      <div className="flex gap-2">
                        <Select
                          value={newItemState.category}
                          onValueChange={(value) => setNewItemState((prev) => ({ ...prev, category: value }))}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Selecione uma categoria" />
                          </SelectTrigger>
                          <SelectContent>
                            {categoriesData.map((category) => (
                              <SelectItem key={category.id} value={category.name}>
                                {category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <AddCategoryDialog
                          onAddCategory={handleAddCategory}
                          categories={categoriesData.map(c => c.name)}
                          trigger={
                            <Button variant="outline" size="icon" type="button">
                              <Plus className="w-4 h-4" />
                            </Button>
                          }
                        />
                      </div>
                    </div>
              </div>

                  {/* Especificações por Categoria */}
                  {newItemState.category && (
                    <SpecificationFields
                      categoryName={newItemState.category}
                      onSpecificationsChange={(specs) => setNewItemState((prev) => ({ ...prev, especificacoes: specs }))}
                      initialValues={newItemState.especificacoes}
                    />
                  )}

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Tipo *</Label>
                      <Select
                        value={newItemState.itemType}
                        onValueChange={(value: InventoryItemType) =>
                          setNewItemState((prev) => ({
                            ...prev,
                            itemType: value,
                            unit: value === "tecido" ? "metros" : prev.unit,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {ITEM_TYPE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              <div className="flex flex-col">
                                <span>{option.label}</span>
                                <span className="text-xs text-muted-foreground">{option.description}</span>
                    </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                  </div>
                    <div className="space-y-2">
                      <Label>Fornecedor</Label>
                      <Input
                        value={newItemState.supplier}
                        onChange={(event) => setNewItemState((prev) => ({ ...prev, supplier: event.target.value }))}
                        placeholder="Opcional"
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label>Quantidade *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newItemState.quantity}
                        onChange={(event) => setNewItemState((prev) => ({ ...prev, quantity: event.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unidade *</Label>
                      <Input
                        value={newItemState.unit}
                        onChange={(event) => setNewItemState((prev) => ({ ...prev, unit: event.target.value }))}
                        placeholder="un, kg, m..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Mínimo</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newItemState.minQuantity}
                        onChange={(event) => setNewItemState((prev) => ({ ...prev, minQuantity: event.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Custo unitário</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newItemState.costPerUnit}
                        onChange={(event) => setNewItemState((prev) => ({ ...prev, costPerUnit: event.target.value }))}
                        placeholder="Ex: 45,90"
                      />
                    </div>
                    {newItemState.itemType === "tecido" && (
                      <div className="space-y-2">
                        <Label>Metragem total (m)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newItemState.lengthMeters}
                          onChange={(event) => setNewItemState((prev) => ({ ...prev, lengthMeters: event.target.value }))}
                        />
                      </div>
                    )}
                  </div>

                  {newItemState.itemType === "tecido" && (
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Cor</Label>
                        <Input
                          value={newItemState.color}
                          onChange={(event) => setNewItemState((prev) => ({ ...prev, color: event.target.value }))}
                        />
                    </div>
                  </div>
                  )}

                  <div className="space-y-2">
                    <Label>Observações</Label>
                    <Textarea
                      value={newItemState.notes}
                      onChange={(event) => setNewItemState((prev) => ({ ...prev, notes: event.target.value }))}
                      placeholder="Informações adicionais sobre o item"
                      rows={3}
                    />
                  </div>

                  <div className="flex items-center space-x-2 pt-2">
                    <Checkbox
                      id="createProductFromItem"
                      checked={createProductFromItem}
                      onCheckedChange={(checked) => setCreateProductFromItem(checked === true)}
                    />
                    <Label
                      htmlFor="createProductFromItem"
                      className="text-sm font-normal cursor-pointer"
                    >
                      Criar item no catálogo
                    </Label>
                  </div>
                </div>
                <DialogFooter className="sticky bottom-0 z-10 bg-card pt-4">
                  <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button variant="outline" onClick={() => setNewItemModalOpen(false)} className="w-full sm:w-auto">
                      Cancelar
                    </Button>
                    <Button onClick={handleCreateItem} className="w-full sm:w-auto">
                      Salvar item
                    </Button>
                  </div>
                </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {showOnboardingHint && (
        <div className="mx-auto max-w-6xl px-6 pt-4">
          <Card className="border border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
            <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-purple-600" />
                <CardTitle className="text-sm font-semibold text-gray-900">
                  Estoque organizado = menos estresse no dia a dia
                </CardTitle>
              </div>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={dismissOnboardingHint}
                aria-label="Fechar dica de estoque"
              >
                <X className="w-4 h-4" />
              </button>
            </CardHeader>
            <CardContent className="pt-0 text-xs md:text-sm text-gray-700 space-y-1">
              <p>
                Aqui você registra o que tem de matéria-prima, tecidos e produtos prontos. Com isso, fica muito mais fácil saber
                se dá para aceitar um pedido grande ou se precisa comprar antes.
              </p>
              <p>
                Não precisa cadastrar tudo de uma vez: comece pelos itens que mais usa. Aos poucos seu estoque fica redondo e você
                para de ser surpreendida por falta de material.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <main className="mx-auto max-w-6xl space-y-6 px-6 py-6">
        <section className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Valor total</CardDescription>
              <CardTitle className="text-2xl">
                {summary.totalValue > 0 ? formatCurrency({ value: summary.totalValue, currency: "BRL" }) : "R$ 0,00"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Somatório considerando custo unitário informado</p>
                </CardContent>
              </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Itens cadastrados</CardDescription>
              <CardTitle className="text-2xl">{summary.totalItems}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{summary.totalQuantity.toFixed(2)} unidades no estoque</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Estoque baixo</CardDescription>
              <CardTitle className="text-2xl text-amber-600">{summary.lowCount}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Itens abaixo do nível mínimo</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Estoque crítico</CardDescription>
              <CardTitle className="text-2xl text-destructive">{summary.criticalCount}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Itens zerados ou negativos</p>
                </CardContent>
              </Card>
        </section>

        <section className="grid gap-4 md:grid-cols-3">
          {ITEM_TYPE_OPTIONS.map((option) => {
            const info = summary.byType[option.value];
            return (
              <Card key={option.value} className="border-dashed">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold">{option.label}</CardTitle>
                    <Badge variant="outline" className="bg-muted/30">
                      {info.count} itens
                    </Badge>
          </div>
                  <CardDescription>{option.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Valor estimado{" "}
                    <span className="font-semibold text-foreground">
                      {info.value > 0 ? formatCurrency({ value: info.value, currency: "BRL" }) : "R$ 0,00"}
                    </span>
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </section>

        <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-1 items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Input
                className="max-w-sm"
                placeholder="Buscar por nome, fornecedor ou categoria..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            {!isSelecting && (
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <Select value={categoryFilter} onValueChange={(value) => setCategoryFilter(value)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as categorias</SelectItem>
                    {categoriesData.map((category) => (
                      <SelectItem key={category.id} value={category.name}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value as InventoryItemType | "all")}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    {ITEM_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isSelecting && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {selectedItems.length} selecionado(s)
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={selectedItems.length === filteredItems.length ? deselectAll : selectAll}
                >
                  {selectedItems.length === filteredItems.length ? "Desmarcar Todos" : "Selecionar Todos"}
                </Button>
              </div>
            )}
          </div>

          <Separator className="my-4" />

          {isLoading ? (
            <div className="flex min-h-[240px] items-center justify-center">
              <PackageSearch className="mr-2 h-5 w-5 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Carregando estoque...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 text-center">
              <PackageSearch className="h-10 w-10 text-muted-foreground" />
              <div>
                <p className="font-medium text-foreground">Nenhum item encontrado</p>
                <p className="text-sm text-muted-foreground">Cadastre um item ou ajuste os filtros de busca</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredItems.map((item) => {
                const statusInfo = computeStatusInfo(item.status);
                const metadata = (item.metadata ?? {}) as Record<string, unknown>;
                const totalValue = item.total_cost ?? (item.cost_per_unit ?? 0) * (item.quantity ?? 0);
            
            return (
              <Card
                    key={item.id}
                    className="border border-border/70 transition-all hover:border-primary/60 hover:shadow-md"
              >
                    <CardContent className="space-y-4 p-4">
                      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            {isSelecting && (
                              <Checkbox
                                checked={selectedItems.includes(item.id)}
                                onCheckedChange={() => toggleItemSelection(item.id)}
                                className="mt-1"
                              />
                            )}
                            <h3 className="text-lg font-semibold text-foreground">{item.name}</h3>
                            <Badge variant="secondary">{getItemTypeLabel(item.item_type)}</Badge>
                            {statusInfo.badge}
                          </div>
                        <p className="text-sm text-muted-foreground">
                            Unidade mínima: <span className="font-medium text-foreground">{item.min_quantity ?? 0}</span>{" "}
                            {item.unit}
                        </p>
                          {item.supplier && (
                            <p className="text-sm text-muted-foreground">
                              Fornecedor: <span className="font-medium text-foreground">{item.supplier}</span>
                            </p>
                          )}
                          {item.category && (
                        <p className="text-sm text-muted-foreground">
                              Categoria: <span className="font-medium text-foreground">{item.category}</span>
                        </p>
                          )}
                          {metadata?.notes && (
                            <p className="text-xs text-muted-foreground/90">Obs.: {String(metadata.notes)}</p>
                          )}
                    </div>

                        <div className="flex items-center gap-6">
                      <div className="text-right">
                            <p className="text-2xl font-semibold text-foreground">
                              {Number(item.quantity ?? 0).toFixed(2)}
                            </p>
                        <p className="text-xs text-muted-foreground">{item.unit}</p>
                            {item.cost_per_unit && (
                              <p className="text-xs text-muted-foreground/90">
                                {formatCurrency({ value: item.cost_per_unit, currency: "BRL" })}/un
                              </p>
                            )}
                            {totalValue > 0 && (
                              <p className="text-xs font-medium text-foreground">
                                Total: {formatCurrency({ value: totalValue, currency: "BRL" })}
                              </p>
                            )}
                      </div>
                          <div className="flex flex-col items-end gap-2">
                            <Button variant="outline" size="icon" onClick={() => handleEditItem(item)}>
                              <Edit className="h-4 w-4" />
                        </Button>
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteItem(item)}>
                              <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                      <Separator />

                      <div className="grid gap-4 md:grid-cols-4">
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Status</p>
                          <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                            {statusInfo.icon}
                            {statusInfo.message}
                          </p>
                        </div>
                        {item.item_type === "tecido" && (
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">Metragem disponível</p>
                            <p className="text-sm font-medium text-foreground">
                              {metadata?.length_meters ? `${metadata.length_meters} m` : "-"}
                            </p>
                          </div>
                        )}
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Atualizado</p>
                          <p className="text-sm font-medium text-foreground">
                            {item.updated_at ? new Date(item.updated_at).toLocaleString("pt-BR") : "-"}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Código interno</p>
                          <p className="text-sm font-mono text-muted-foreground/80">{item.id}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
          )}
        </section>
      </main>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sticky top-0 z-10 bg-card pb-4">
            <DialogTitle>Editar item</DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 py-2">
            <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
                <Label>Nome *</Label>
              <Input
                value={editForm.name}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Input
                  value={editForm.category}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, category: event.target.value }))}
                />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select
                  value={editForm.itemType}
                  onValueChange={(value: InventoryItemType) =>
                    setEditForm((prev) => ({
                      ...prev,
                      itemType: value,
                      unit: value === "tecido" ? "metros" : prev.unit,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ITEM_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fornecedor</Label>
                <Input
                  value={editForm.supplier}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, supplier: event.target.value }))}
                />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Quantidade *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.quantity}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, quantity: event.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Unidade *</Label>
                <Input
                  value={editForm.unit}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, unit: event.target.value }))}
                />
            </div>
            <div className="space-y-2">
                <Label>Mínimo</Label>
              <Input
                type="number"
                  min="0"
                  step="0.01"
                value={editForm.minQuantity}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, minQuantity: event.target.value }))}
              />
            </div>
          </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Custo unitário</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.costPerUnit}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, costPerUnit: event.target.value }))}
                />
              </div>
              {editForm.itemType === "tecido" && (
                <div className="space-y-2">
                  <Label>Metragem total (m)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.lengthMeters}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, lengthMeters: event.target.value }))}
                  />
                </div>
              )}
            </div>

            {editForm.itemType === "tecido" && (
              <div className="space-y-2">
                <Label>Cor</Label>
                <Input
                  value={editForm.color}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, color: event.target.value }))}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea
                rows={3}
                value={editForm.notes}
                onChange={(event) => setEditForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 z-10 bg-card pt-4">
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} className="w-full sm:w-auto">
              Cancelar
            </Button>
              <Button onClick={handleSaveEdit} className="w-full sm:w-auto">
                Salvar alterações
            </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
